import { Injectable, inject, InjectionToken } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments';

declare const grecaptcha: {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
};

const SCRIPT_ID = 'recaptcha-v3-script';

export const RECAPTCHA_SITE_KEY = new InjectionToken<string>('RECAPTCHA_SITE_KEY', {
  providedIn: 'root',
  factory: () => environment.RECAPTCHA_KEY
});

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private readonly document = inject(DOCUMENT);
  private readonly siteKey = inject(RECAPTCHA_SITE_KEY);
  private loaded = false;


  execute(action: string): Promise<string> {
    return this.ensureLoaded().then(() => {
      return new Promise<string>((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(this.siteKey, { action })
            .then(resolve)
            .catch(reject);
        });
      });
    });
  }

  private ensureLoaded(): Promise<void> {
    if (this.loaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (this.document.getElementById(SCRIPT_ID)) {
        this.loaded = true;
        resolve();
        return;
      }

      const script = this.document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.loaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));

      this.document.head.appendChild(script);
    });
  }
}
